import { Alert, AlertTitle, Box, Paper, Popper, Typography } from '@mui/material'
import type { DateTime } from 'luxon'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { FormattedRow } from '@/features/bessDashboard/types'
import AreaGraph from '@/features/bidding/components/cascading/charts/elements/AreaGraph'
import type { AreaGraphElement } from '@/features/bidding/components/cascading/charts/types'
import { useHourlyForecastPrices } from '@/features/bidding/hooks/useHourlyForecastPrices'
import type { ForecastMarketPair, HourlyQuantilePrices, PriceQuantile } from '@/features/bidding/types/forecastPricing'
import { QUANTILE_ORDER } from '@/features/bidding/types/forecastPricing'
import { getMarketProgramNames, isSymmetricMarket } from '@/features/bidding/utils/forecast/marketProgramNames'

import { useForecastAreaGraphData } from './useForecastAreaGraphData'

const CHART_HEIGHT = '100px'

type TooltipState = {
  position: { x: number; y: number }
  hour: number
  prices: HourlyQuantilePrices
  direction: 'up' | 'down' | 'symmetric'
} | null

type StackedForecastChartsProps = {
  forecastsData: FormattedRow[] | undefined
  market: ForecastMarketPair
  quantile: PriceQuantile
  selectedDeliveryDay: DateTime
}

function StackedForecastCharts({
  forecastsData,
  market,
  quantile,
  selectedDeliveryDay,
}: Readonly<StackedForecastChartsProps>) {
  const { t } = useTranslation()
  const marketPrograms = getMarketProgramNames(market)
  const isSymmetric = isSymmetricMarket(market)

  const [tooltip, setTooltip] = useState<TooltipState>(null)

  const handleHover = useCallback(
    (direction: 'up' | 'down' | 'symmetric') =>
      (position: { x: number; y: number } | null, dataPoint: AreaGraphElement | null) => {
        if (!position || !dataPoint) {
          setTooltip(null)
          return
        }
        const prices = dataPoint.meta.prices as HourlyQuantilePrices | undefined
        if (!prices) {
          setTooltip(null)
          return
        }
        setTooltip({
          position,
          hour: dataPoint.meta.hour as number,
          prices,
          direction,
        })
      },
    [],
  )

  // Get UP forecast data
  const upForecast = useHourlyForecastPrices({
    forecastsData,
    marketProgramName: marketPrograms.up,
    selectedDate: selectedDeliveryDay,
  })

  // Get DOWN forecast data (only needed for asymmetric markets)
  const downForecast = useHourlyForecastPrices({
    forecastsData,
    marketProgramName: marketPrograms.down,
    selectedDate: selectedDeliveryDay,
  })

  // Transform data for AreaGraph (includes value-based layerFill)
  const upGraphData = useForecastAreaGraphData({
    dailyPrices: upForecast.dailyPrices,
    selectedQuantile: quantile,
    direction: 'up',
  })

  const downGraphData = useForecastAreaGraphData({
    dailyPrices: downForecast.dailyPrices,
    selectedQuantile: quantile,
    direction: 'down',
  })

  // For symmetric markets, only check UP data; for asymmetric, need BOTH directions
  const hasNoData = isSymmetric ? !upForecast.hasData : !upForecast.hasData || !downForecast.hasData

  if (hasNoData) {
    return (
      <Box
        sx={{
          p: 2,
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
          {t('bidding.bidding_cascading.configuration.no_forecast_data')}
        </Typography>
      </Box>
    )
  }

  // Check for incomplete data (after hasData check)
  const upIncomplete = upForecast.hasData && !upForecast.isComplete
  const downIncomplete = !isSymmetric && downForecast.hasData && !downForecast.isComplete

  if (upIncomplete || downIncomplete) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="warning">
          <AlertTitle>{t('bidding.bidding_cascading.configuration.incomplete_forecast_data')}</AlertTitle>
          {upIncomplete && (
            <Typography sx={{ mt: 1 }} variant="body2">
              {t('bidding.bidding_cascading.configuration.missing_hours_up', {
                available: upForecast.dailyPrices?.hourlyPrices.size ?? 0,
                total: upForecast.dailyPrices?.dayHours ?? 24,
                missingHours: upForecast.missingHours.join(', '),
              })}
            </Typography>
          )}
          {downIncomplete && (
            <Typography sx={{ mt: 1 }} variant="body2">
              {t('bidding.bidding_cascading.configuration.missing_hours_down', {
                available: downForecast.dailyPrices?.hourlyPrices.size ?? 0,
                total: downForecast.dailyPrices?.dayHours ?? 24,
                missingHours: downForecast.missingHours.join(', '),
              })}
            </Typography>
          )}
        </Alert>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      {/* Header with market name, selected quantile, and unit */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 0.5 }}>
        <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>{marketPrograms.displayName}</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: 'text.primary' }}>
            {quantile.toUpperCase()}
          </Typography>
          <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
            {t('bidding.bidding_cascading.configuration.price_unit')}
          </Typography>
        </Box>
      </Box>

      {/* For symmetric markets, show single chart; for asymmetric, show UP and DOWN */}
      {isSymmetric ? (
        // Single chart for symmetric markets (e.g., FCR-N)
        <Box>
          {upGraphData.data.length > 0 ? (
            <AreaGraph
              data={upGraphData.data}
              datetimes={upGraphData.datetimes}
              formatTick={(v) => v.toFixed(2)}
              height={CHART_HEIGHT}
              layerFill={upGraphData.layerFill}
              layerKeys={upGraphData.layerKeys}
              overlayLines={upGraphData.overlayLines}
              testId="forecast-chart-symmetric"
              onHover={handleHover('symmetric')}
            />
          ) : (
            <Box
              sx={{
                height: CHART_HEIGHT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'grey.50',
                borderRadius: 0.5,
              }}
            >
              <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
                {t('bidding.bidding_cascading.configuration.chart_no_data')}
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        // Stacked UP/DOWN charts for asymmetric markets
        <>
          {/* UP Chart */}
          <Box>
            <Typography
              sx={{
                fontSize: '0.5625rem',
                fontWeight: 600,
                color: 'success.main',
                px: 0.5,
                mb: 0.25,
              }}
            >
              {t('bidding.bidding_cascading.configuration.direction_up')}
            </Typography>
            {upGraphData.data.length > 0 ? (
              <AreaGraph
                data={upGraphData.data}
                datetimes={upGraphData.datetimes}
                formatTick={(v) => v.toFixed(2)}
                height={CHART_HEIGHT}
                layerFill={upGraphData.layerFill}
                layerKeys={upGraphData.layerKeys}
                overlayLines={upGraphData.overlayLines}
                testId="forecast-chart-up"
                onHover={handleHover('up')}
              />
            ) : (
              <Box
                sx={{
                  height: CHART_HEIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'grey.50',
                  borderRadius: 0.5,
                }}
              >
                <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
                  {t('bidding.bidding_cascading.configuration.chart_no_up_data')}
                </Typography>
              </Box>
            )}
          </Box>

          {/* DOWN Chart */}
          <Box>
            <Typography
              sx={{
                fontSize: '0.5625rem',
                fontWeight: 600,
                color: 'error.main',
                px: 0.5,
                mb: 0.25,
              }}
            >
              {t('bidding.bidding_cascading.configuration.direction_down')}
            </Typography>
            {downGraphData.data.length > 0 ? (
              <AreaGraph
                data={downGraphData.data}
                datetimes={downGraphData.datetimes}
                formatTick={(v) => v.toFixed(2)}
                height={CHART_HEIGHT}
                layerFill={downGraphData.layerFill}
                layerKeys={downGraphData.layerKeys}
                overlayLines={downGraphData.overlayLines}
                testId="forecast-chart-down"
                onHover={handleHover('down')}
              />
            ) : (
              <Box
                sx={{
                  height: CHART_HEIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'grey.50',
                  borderRadius: 0.5,
                }}
              >
                <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
                  {t('bidding.bidding_cascading.configuration.chart_no_down_data')}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Tooltip */}
      <Popper
        anchorEl={
          tooltip
            ? {
                getBoundingClientRect: () => ({
                  top: tooltip.position.y,
                  left: tooltip.position.x,
                  bottom: tooltip.position.y,
                  right: tooltip.position.x,
                  width: 0,
                  height: 0,
                  x: tooltip.position.x,
                  y: tooltip.position.y,
                  toJSON: () => ({}),
                }),
              }
            : null
        }
        modifiers={[{ name: 'offset', options: { offset: [74, -10] } }]}
        open={tooltip !== null}
        placement="top-start"
        sx={{ zIndex: 1500, pointerEvents: 'none' }}
      >
        {tooltip && (
          <Paper
            elevation={3}
            sx={{
              p: 1,
              minWidth: 140,
              backgroundColor: 'background.paper',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600 }}>
                {String(tooltip.hour).padStart(2, '0')}:00
                {tooltip.direction !== 'symmetric' && ` (${tooltip.direction.toUpperCase()})`}
              </Typography>
              <Typography sx={{ fontSize: '0.5625rem', color: 'text.secondary' }}>
                {t('bidding.bidding_cascading.configuration.price_unit')}
              </Typography>
            </Box>
            {[...QUANTILE_ORDER].reverse().map((q) => (
              <Box
                key={q}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 1,
                  backgroundColor: q === quantile ? 'action.selected' : 'transparent',
                  px: 0.5,
                  borderRadius: 0.5,
                }}
              >
                <Typography sx={{ fontSize: '0.625rem', color: 'text.secondary' }}>{q.toUpperCase()}</Typography>
                <Typography sx={{ fontSize: '0.625rem', fontWeight: q === quantile ? 600 : 400 }}>
                  {tooltip.prices[q].toFixed(2)}
                </Typography>
              </Box>
            ))}
          </Paper>
        )}
      </Popper>
    </Box>
  )
}

export default StackedForecastCharts
